/* eslint-disable no-console */
import { Context2d, jsPDF } from 'jspdf';

import theme from '../../assets/theme';
import { SlideElementShapeType } from '../../components/SlideComponents/slide.types';
import { parseRGB } from './utils';

export const addShape = async (pdf: jsPDF, element: SlideElementShapeType) => {
  const getClipFunction = (clipPath: string | undefined) => {
    if (clipPath) {
      const type = clipPath.substring(0, 10).match(/polygon|ellipse|circle|inset|(|)/);

      if (Array.isArray(type) && type.length) return type[0];
    }
    return null;
  };

  const normalizeInput = (clipPath: string) => {
    const regex = /-?\d+(\.\d+)?/g;

    // Use the match method to find all the numbers in the string
    const numbersArray = clipPath.match(regex);

    // Convert the matched numbers from strings to actual numbers
    if (numbersArray) {
      return numbersArray.map(parseFloat);
    }
    return [];
  };

  const drawPolygon = (ctx: Context2d, shape: SlideElementShapeType) => {
    const { shapeClipPath, x, y, colors, width, height } = shape;
    if (shapeClipPath) {
      const polyPath = normalizeInput(shapeClipPath);
      // Save current context so we can restore it when drawing is finished
      ctx.save();
      // Add x,y to shape's coords
      ctx.translate(x, y);
      // Rotate by defined angle - breaks the rendering in some scenarios
      let color = theme.colors.greyDark;
      if (colors?.length) {
        color = colors?.[0];
      }
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.moveTo((polyPath[0] * width) / 100, (polyPath[1] * height) / 100);
      for (let i = 2; i < polyPath.length - 1; i += 2) {
        ctx.lineTo(polyPath[i] * (width / 100), polyPath[i + 1] * (height / 100));
      }
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    }
  };

  const drawEllipse = (pdf: jsPDF, shape: SlideElementShapeType) => {
    const { shapeClipPath, x, y, colors, width, height } = shape;
    if (!shapeClipPath) return;
    const rgb = parseRGB(colors[0]);
    if (rgb) {
      const [r, g, b] = rgb;
      pdf.setFillColor(r, g, b);
    }

    let startPos = [50, 50];
    let radius;
    if (shapeClipPath.includes(' at ')) {
      const [radiusString, positionString] = shapeClipPath.split(' at ');
      radius = normalizeInput(radiusString);
      startPos = normalizeInput(positionString);
    } else {
      radius = normalizeInput(shapeClipPath);
    }
    const ellipseX = x + (width * startPos[0]) / 100;
    const ellipseY = y + (height * startPos[1]) / 100;
    const ellipseWidth = (width / 100) * radius[0];
    const ellipseHeight = (height / 100) * radius[1];
    pdf.saveGraphicsState();
    pdf.rect(x, y, width, height, null);
    pdf.clip();
    pdf.discardPath();
    pdf.ellipse(ellipseX, ellipseY, ellipseWidth, ellipseHeight, 'F');
    pdf.restoreGraphicsState();
  };

  const drawRect = (pdf: jsPDF, element: SlideElementShapeType) => {
    const { x, y, colors, width, height } = element;
    const rgb = parseRGB(colors[0]);
    if (rgb) {
      const [r, g, b] = rgb;
      pdf.setFillColor(r, g, b);
    }
    pdf.rect(x, y, width, height, 'F');
  };

  const drawRoundedRect = (pdf: jsPDF, element: SlideElementShapeType) => {
    const { x, y, colors, width, height } = element;
    const rgb = parseRGB(colors[0]);
    if (rgb) {
      const [r, g, b] = rgb;
      pdf.setFillColor(r, g, b);
    }
    pdf.roundedRect(x, y, width, height, 20, 20, 'F');
  };

  const ctx = pdf.context2d;
  if (element.shapeClipPath) {
    switch (getClipFunction(element.shapeClipPath)) {
      case 'polygon':
        drawPolygon(ctx, element);
        break;
      case 'ellipse':
        drawEllipse(pdf, element);
        break;
      case 'inset':
        drawRoundedRect(pdf, element);
        break;
      default:
        console.log('invalid shape');
    }
  } else {
    // No shapeClipPath means that it's just basic rectangle
    drawRect(pdf, element);
  }
};
